import type { NiceAxis } from '@ardoq/math';
import { toAngle, toAngularAxis, toRadialAxis } from './radialAxis';
import type { RadialPlot, RadialSeries } from './types';

export type PolarDatum = {
  key: string;
  label: string;
  seriesKey: string;
  seriesLabel: string;
  angleValue: number;
  radiusValue: number;
};

export type PolarPlotArgs = {
  data: PolarDatum[];
  angleTitle: string;
  radialTitle: string;
};

const byAngleValue = (a: PolarDatum, b: PolarDatum): number =>
  a.angleValue - b.angleValue;

const byPointCount = (a: RadialSeries, b: RadialSeries): number =>
  b.points.length - a.points.length;

const toSeries = (angularAxis: NiceAxis, data: PolarDatum[]): RadialSeries => ({
  key: data[0].seriesKey,
  label: data[0].seriesLabel,
  points: data.toSorted(byAngleValue).map(datum => ({
    key: datum.key,
    label: datum.label,
    angle: toAngle(angularAxis, datum.angleValue),
    value: datum.radiusValue,
    angleValue: datum.angleValue,
  })),
});

/**
 * Series ordered largest first, so the categorical slots go to the series with the most points.
 * Equal sizes keep the order in which their series first appear in `data`.
 */
export const toPolarPlot = ({
  data,
  angleTitle,
  radialTitle,
}: PolarPlotArgs): RadialPlot => {
  const angularAxis = toAngularAxis(data.map(({ angleValue }) => angleValue));

  return {
    series: [...Map.groupBy(data, ({ seriesKey }) => seriesKey).values()]
      .map(group => toSeries(angularAxis, group))
      .toSorted(byPointCount),
    angularAxis: { kind: 'numeric', axis: angularAxis, title: angleTitle },
    radialAxis: toRadialAxis(data.map(({ radiusValue }) => radiusValue)),
    radialTitle,
  };
};
